import { FormattedMessage } from 'react-intl';
import { getFile } from '../../../../../services/api/files';
import { PropTypes } from 'prop-types';
import EXIF from 'exif-js';

const ResponseRow = ({ title, value }) => (
  <tr>
    <td
      colSpan="9"
      style={{
        backgroundColor: '#e6e6e6',
        textAlign: 'left',
        width: '75%',
      }}
    >
      <label style={{ fontSize: '17px' }}>{title}</label>
    </td>

    <td
      colSpan="3"
      style={{
        backgroundColor: '#FFFFFF',
        textAlign: 'left',
        width: '25%',
      }}
    >
      <span style={{ fontSize: '17px' }}>{value}</span>
    </td>
  </tr>
);

ResponseRow.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const TitleRow = ({ title }) => (
  <tr>
    <td
      colSpan="12"
      style={{
        backgroundColor: '#e6e6e6',
        textAlign: 'center',
        width: '100%',
      }}
    >
      <label style={{ fontSize: '17px' }}>{title}</label>
    </td>
  </tr>
);

TitleRow.propTypes = {
  title: PropTypes.string.isRequired,
};

const CommentRow = ({ values }) => {
  return (
    <tr>
      <td
        colSpan="3"
        style={{
          backgroundColor: '#e6e6e6',
          textAlign: 'left',
          width: '25%',
        }}
      >
        <label style={{ fontSize: '17px' }}>
          <FormattedMessage id="Comments" />
        </label>
      </td>
      <td
        colSpan="9"
        style={{
          backgroundColor: '#ffffff',
          textAlign: 'left',
          width: '75%',
        }}
      >
        {values.map((value, index) =>
          value ? (
            <p
              key={index}
              style={index === values.length - 1 ? { marginBottom: '0' } : {}}
            >
              <span style={{ fontSize: '17px' }}>{`${
                index + 1
              }. ${value}`}</span>
            </p>
          ) : null,
        )}
      </td>
    </tr>
  );
};

CommentRow.propTypes = {
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const WhiteRow = () => {
  return (
    <tr>
      <td
        colSpan="12"
        style={{
          backgroundColor: '#ECECEC',
          textAlign: 'left',
          width: '100%',
        }}
      ></td>
    </tr>
  );
};

const imgSetter = (arrayLen, name, setFunction, path) => {
  for (var i = 0; i < arrayLen; i++)
    getFile(`${name}_${i}`, 'PRM').then(({ data }) => {
      setFunction((prev) => [...prev, data]);
    });
};

const getFixedOrientationImageUrl = (orientation, img) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  let height = img.height;
  let width = img.width;

  if (img.width > 1000) {
    width = 1000;
    height = (1000 * img.width) / img.height;
  }

  if (orientation > 4) {
    canvas.width = height;
    canvas.height = width;
  } else {
    canvas.width = width;
    canvas.height = height;
  }

  switch (orientation) {
    case 3:
      ctx.rotate(Math.PI);
      ctx.drawImage(
        img,
        -canvas.width,
        -canvas.height,
        canvas.width,
        canvas.height,
      );
      break;
    case 6:
      ctx.rotate(0.5 * Math.PI);
      ctx.drawImage(img, 0, -canvas.width, canvas.height, canvas.width);
      break;
    case 8:
      ctx.rotate(-0.5 * Math.PI);
      ctx.drawImage(img, -canvas.height, 0, canvas.height, canvas.width);
      break;
    default:
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      break;
  }
  return canvas.toDataURL('image/png');
};

const getDimensions = (width, height, pdf, fullSize) => {
  return new Promise((resolve, reject) => {
    try {
      let result;
      const fullWidth = pdf.internal.pageSize.getWidth() - 110;
      const pageWidth = fullSize ? fullWidth : fullWidth / 2 - 2;
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageRatio = pageWidth / pageHeight;

      const imgWidth = width;
      const imgHeight = height;
      const imgRatio = imgWidth / imgHeight;

      if (imgRatio >= 1) {
        // La imagen es cuadrada o apaisada
        if (imgWidth < pageWidth) {
          result = {
            width: imgWidth,
            height: imgHeight,
          };
        } else {
          const wc = imgWidth / pageWidth;
          if (imgRatio >= pageRatio) {
            result = {
              width: pageWidth,
              height: imgHeight / wc,
            };
          } else {
            const pi = pageRatio / imgRatio;

            result = {
              width: pageWidth / pi,
              height: imgHeight / pi / wc,
            };
          }
        }
      } else {
        // La foto es vertical
        const wc = imgWidth / pageHeight;
        if (1 / imgRatio > pageRatio) {
          const ip = 1 / imgRatio / pageRatio;
          result = {
            width: pageHeight / ip,
            height: imgHeight / ip / wc,
          };
        } else {
          result = {
            width: pageHeight,
            height: imgHeight / wc,
          };
        }
      }
      resolve(result);
    } catch (e) {
      reject(e);
    }
  });
};

const getDimensionsAndUrl = async (url, pdf, fullSize) => {
  return new Promise((resolve, reject) => {
    try {
      let img = new Image();
      img.src = url;
      img.onload = async function () {
        const orientation = EXIF.getTag(this, 'Orientation');

        const url = getFixedOrientationImageUrl(orientation, img);
        const dimensions = await getDimensions(
          this.width,
          this.height,
          pdf,
          fullSize,
        );
        resolve({ url, ...dimensions });
      };
    } catch (e) {
      reject(e);
    }
  });
};

const calcImagesDimensionsAndUrl = async (images, pdf) => {
  const prm_q1 = await Promise.all(
    images.prm_q1.map((image) =>
      getDimensionsAndUrl(URL.createObjectURL(image), pdf, true),
    ),
  );
  const prm_q2 = await Promise.all(
    images.prm_q2.map((image) =>
      getDimensionsAndUrl(
        URL.createObjectURL(image),
        pdf,
        images.prm_q2.length === 1,
      ),
    ),
  );
  const prm_q3 = await Promise.all(
    images.prm_q3.map((image) =>
      getDimensionsAndUrl(
        URL.createObjectURL(image),
        pdf,
        images.prm_q3.length === 1,
      ),
    ),
  );
  const prm_q4 = await Promise.all(
    images.prm_q4.map((image) =>
      getDimensionsAndUrl(
        URL.createObjectURL(image),
        pdf,
        images.prm_q4.length === 1,
      ),
    ),
  );
  const signature = await Promise.all(
    images.signature.map((image) =>
      getDimensionsAndUrl(URL.createObjectURL(image), pdf),
    ),
  );

  return { prm_q1, prm_q2, prm_q3, prm_q4, signature };
};

const insertGallery = (images, type) => {
  return images.reduce((res, elem, index) => {
    if (index % 2 === 0) {
      res.push([
        {
          content: type + '__' + index,
          colSpan: 2,
          styles: { minCellHeight: 100 },
        },
        images[index + 1]
          ? {
              content: type + '__' + (index + 1),
              colSpan: 2,
              styles: { minCellHeight: 100 },
            }
          : {
              colSpan: 2,
              styles: { minCellHeight: 100 },
              content: '',
            },
      ]);
    }
    return res;
  }, []);
};

export {
  imgSetter,
  WhiteRow,
  CommentRow,
  ResponseRow,
  TitleRow,
  getDimensionsAndUrl,
  calcImagesDimensionsAndUrl,
  insertGallery,
};
